import { Node, mergeAttributes } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { closeHistory } from '@tiptap/pm/history';
import { DEFAULT_CARD_LAYOUT, normalizeCardLayout } from '../../general/card-group-layout';
import type { CardGroupLayout } from '../../general/card-group-layout';

export const CardGroupNode = Node.create({
    name: 'cardGroup', group: 'block', content: 'editorCard+', defining: true, isolating: true,
    addAttributes() { return Object.fromEntries(Object.entries(DEFAULT_CARD_LAYOUT).map(([key, value]) => [key, { default: value }])); },
    parseHTML() { return [{ tag: 'div[data-type="card-group"]', getAttrs: element => normalizeCardLayout({
        columns: Number(element.getAttribute('data-columns') ?? 3), mobileColumns: Number(element.getAttribute('data-mobile-columns') ?? 1),
        gap: Number(element.getAttribute('data-gap') ?? 16), mobileGap: Number(element.getAttribute('data-mobile-gap') ?? 12),
        padding: Number(element.getAttribute('data-padding') ?? 16), mobilePadding: Number(element.getAttribute('data-mobile-padding') ?? 12),
        equalHeight: element.getAttribute('data-equal-height') !== 'false', variant: element.getAttribute('data-card-variant') ?? 'default'
    }) }]; },
    renderHTML({ node, HTMLAttributes }) {
        const layout = normalizeCardLayout(node.attrs);
        const other = Object.fromEntries(Object.entries(HTMLAttributes).filter(([key]) => !(key in DEFAULT_CARD_LAYOUT)));
        return ['div', mergeAttributes(other, { 'data-type': 'card-group', class: 'lesson-card-group',
            'data-columns': layout.columns, 'data-mobile-columns': layout.mobileColumns, 'data-gap': layout.gap, 'data-mobile-gap': layout.mobileGap,
            'data-padding': layout.padding, 'data-mobile-padding': layout.mobilePadding, 'data-equal-height': String(layout.equalHeight), 'data-card-variant': layout.variant,
            style: `--card-columns:${layout.columns};--card-mobile-columns:${layout.mobileColumns};--card-gap:${layout.gap}px;--card-mobile-gap:${layout.mobileGap}px;--card-padding:${layout.padding}px;--card-mobile-padding:${layout.mobilePadding}px;`
        }), 0];
    }
});
export const EditorCardNode = Node.create({
    name: 'editorCard', content: 'block+', defining: true, isolating: true,
    parseHTML() { return [{ tag: 'div[data-type="editor-card"]' }]; },
    renderHTML({ HTMLAttributes }) { return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'editor-card', class: 'lesson-card' }), 0]; }
});

export function cardContext(editor: Editor) {
    const { $from, from } = editor.state.selection;
    const selected = editor.state.doc.nodeAt(from);
    if (selected?.type.name === 'cardGroup') return { group: selected, pos: from, index: 0 };
    for (let depth = $from.depth; depth > 0; depth--) {
        if ($from.node(depth).type.name === 'cardGroup') return { group: $from.node(depth), pos: $from.before(depth), index: $from.index(depth) };
    }
    return null;
}
function emptyCard(editor: Editor, number: number) {
    const schema = editor.schema;
    return schema.nodes.editorCard.create(null, [schema.nodes.heading.create({ level: 3 }, schema.text(`Card ${number}`)), schema.nodes.paragraph.create()]);
}
export function insertCardGroup(editor: Editor, layout: CardGroupLayout, count: number) {
    return editor.chain().focus().command(({ tr }) => { closeHistory(tr); return true; }).insertContent([
        { type: 'cardGroup', attrs: normalizeCardLayout(layout), content: Array.from({ length: Math.max(1, Math.min(12, Math.round(count))) }, (_, index) => emptyCard(editor, index + 1).toJSON()) },
        { type: 'paragraph' }
    ]).run();
}
export function addCard(editor: Editor) {
    const context = cardContext(editor);
    if (!context) return;
    const pos = context.pos + context.group.nodeSize - 1;
    const tr = editor.state.tr.insert(pos, emptyCard(editor, context.group.childCount + 1));
    editor.view.dispatch(closeHistory(tr).setSelection(TextSelection.near(tr.doc.resolve(pos + 2))).scrollIntoView());
    editor.commands.focus();
}
export function changeCard(editor: Editor, action: 'remove' | 'previous' | 'next' | 'ungroup') {
    const context = cardContext(editor);
    if (!context) return;
    const cards = Array.from({ length: context.group.childCount }, (_, i) => context.group.child(i));
    if (action === 'ungroup') {
        const content = Fragment.fromArray(cards.flatMap(card => Array.from({ length: card.childCount }, (_, i) => card.child(i))));
        const tr = editor.state.tr.replaceWith(context.pos, context.pos + context.group.nodeSize, content);
        editor.view.dispatch(closeHistory(tr).setSelection(TextSelection.near(tr.doc.resolve(context.pos + 1))).scrollIntoView());
    } else {
        const index = context.index;
        const target = action === 'previous' ? index - 1 : index + 1;
        if (action === 'remove') { if (cards.length < 2) return; cards.splice(index, 1); }
        else { if (target < 0 || target >= cards.length) return; [cards[index], cards[target]] = [cards[target], cards[index]]; }
        const group = context.group.copy(Fragment.fromArray(cards));
        const selectedIndex = action === 'remove' ? Math.min(index, cards.length - 1) : target;
        const pos = context.pos + 2 + cards.slice(0, selectedIndex).reduce((sum, card) => sum + card.nodeSize, 0);
        const tr = editor.state.tr.replaceWith(context.pos, context.pos + context.group.nodeSize, group);
        editor.view.dispatch(closeHistory(tr).setSelection(TextSelection.near(tr.doc.resolve(pos))).scrollIntoView());
    }
    editor.commands.focus();
}
